import {DomainGraphType} from 'app-core/framework/CoreObjectConstants';
import {getApiBaseForObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {
  ProcedureParameterConfirmationMethod,
  ProcedureRunDataEntryMode,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import type {TypedValue} from 'notebook/tables/TableConstants';
import {patchJSONPromise, postJSONPromise} from 'util/Net';

export interface ProcedureRunPlanReplicateInfoREST {
  replicateNumber: number;
  sourcePMEIIdsByTemplatePMDVId?: Record<string, Array<string>>;
}

export interface PlanDataParameterValueItemREST {
  value: TypedValue | null;
}
export interface PlanMaterialInputValueItemREST {
  value: {
    material: TypedValue | null;
    amount: TypedValue | null;
  };
}
export interface PlanEquipmentValueItemREST {
  value: {
    equipment: TypedValue | null;
  };
}

export interface PlanDataParameterValueREST {
  dataParameterVersionId: string;
  values: Array<PlanDataParameterValueItemREST>;
}

export interface PlanMaterialInputValueREST {
  materialInputVersionId: string;
  values: Array<PlanMaterialInputValueItemREST>;
}

export interface PlanEquipmentValueREST {
  equipmentVersionId: string;
  values: Array<PlanEquipmentValueItemREST>;
}

export interface PlanMethodPlanREST {
  methodVersionId: string;
  dataParameterValues: Array<PlanDataParameterValueREST>;
  materialInputValues: Array<PlanMaterialInputValueREST>;
  equipmentValues: Array<PlanEquipmentValueREST>;
}

export interface ProcedureRunPlanConditionREST {
  id?: string; // Optional for new conditions
  name: string;
  description?: string;
  position: number;
  methodPlans: Array<PlanMethodPlanREST>;
  replicateInfos: Array<ProcedureRunPlanReplicateInfoREST>;
}

export interface UpdateProcedureRunPlanParams {
  id: string;
  studyId: string;
  description?: string;
  experimentConditions?: Array<ProcedureRunPlanConditionREST>;
  confirmationMethodByParamVersionId?: Record<string, ProcedureParameterConfirmationMethod>;
  dataEntryMode?: ProcedureRunDataEntryMode;
  templateProcedureFlowchartVersionId?: string;
  workflowFlowchartConfigVersionId?: string;
}

export interface UpdateProcedureRunPlanResponse {
  updatedIds: Array<string>;
}

export interface CreateProcedureRunPlansParams {
  description: string;
  creatorId: string;
  dataEntryMode: ProcedureRunDataEntryMode;
  templateProcedureFlowchartVersionId: string | null;
  workflowFlowchartConfigVersionId: string | null;
}

export interface CreateProcedureRunPlansResponse {
  createdIds: Array<string>;
}

export interface BulkCreateProcedureRunPlansParam {
  creates: Array<CreateProcedureRunPlansParams>;
}

export function createProcedureRunPlans(
  studyId: string,
  creates: Array<CreateProcedureRunPlansParams>
): Promise<CreateProcedureRunPlansResponse> {
  // Filter out null values to satisfy backend XOR validation for
  // templateProcedureFlowchartVersionId and workflowFlowchartConfigVersionId
  const createsWithoutNulls = creates.map((create) =>
    Object.fromEntries(Object.entries(create).filter(([_, value]) => value != null))
  );

  return postJSONPromise({
    url: `${getApiBaseForObjectType(DomainGraphType.STUDY)}/${studyId}:procedure-run-plans`,
    data: {creates: createsWithoutNulls},
  }) as Promise<CreateProcedureRunPlansResponse>;
}

export async function updateProcedureRunPlan(
  plan: UpdateProcedureRunPlanParams
): Promise<UpdateProcedureRunPlanResponse> {
  const {studyId, ...updateParams} = plan;
  const updates = [updateParams];
  const url = `${getApiBaseForObjectType(DomainGraphType.STUDY)}/${studyId}:procedure-run-plans`;

  const response = await patchJSONPromise({
    url,
    data: {updates},
  });
  return response as UpdateProcedureRunPlanResponse;
}
